import Base from '@studiometa/js-toolkit';
import withIntersectionObserver from '@studiometa/js-toolkit/decorators/withIntersectionObserver';
import anime from 'animejs';

/**
 * @typedef {Object} AnimatedImageRef
 * @property {HTMLElement} img
 * @property {HTMLElement} overlay
 */

/**
 * @typedef {Object} AnimatedImageInterface
 * @property {AnimatedImageRef} $refs
 * @property {IntersectionObserver} $observer
 */

/**
 * Homepage class.
 */
export default class AnimatedImage extends withIntersectionObserver(Base) {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    name: 'AnimatedImage',
    refs: ['img', 'overlay'],
  };

  /**
   * Initialize the component's behavior.
   *
   * @this {AnimatedImage & AnimatedImageInterface}
   */
  mounted() {}

  /**
   * Triggered when the root element of the component is intersecting.
   *
   * @param {Array<IntersectionObserverEntry>} entries
   * @this {AnimatedImage & AnimatedImageInterface}
   */
  intersected(entries) {
    if (entries[0].isIntersecting) {
      this.$observer.unobserve(this.$el);
      this.animate();
    }
  }

  /**
   * Animate the image.
   *
   * @this {AnimatedImage & AnimatedImageInterface}
   */
  animate() {
    this.$refs.overlay.style.opacity = '1';

    const stepOneTime = 800;
    const stepTwoTime = 800;
    const imageTime = 1400;

    const stepOneEasingSuffix = 'Expo';
    const stepTowEasingSuffix = 'Quint';
    const imageEasingSuffix = 'Quart';

    const stepTwoDelay = 0;

    anime({
      targets: this.$refs.overlay,
      translateX: ['-100%', '0%'],
      easing: `easeIn${stepOneEasingSuffix}`,
      duration: stepOneTime,
      complete: () => {
        this.$refs.img.style.opacity = '1';

        anime({
          targets: this.$refs.img,
          translateX: ['-32px', '0px'],
          easing: `easeOut${imageEasingSuffix}`,
          duration: imageTime,
          delay: stepTwoDelay,
        });

        anime({
          targets: this.$refs.overlay,
          translateX: ['0%', '100%'],
          easing: `easeOut${stepTowEasingSuffix}`,
          duration: stepTwoTime,
          delay: stepTwoDelay,
        });
      },
    });
  }

  /**
   * Unbind all events on destroy.
   *
   * @this {AnimatedImage & AnimatedImageInterface}
   * @return {AnimatedImage} The AnimatedImage instance.
   */
  destroyed() {
    return this;
  }
}
